import { useHandler } from "@redotech/react-util/hook";
import { VendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MarketplaceProductsGrid, OrderDetails } from "./components";
import marketplaceBannerUrl from "./marketplace-banner.webp?url";
import * as marketplaceHomeCss from "./marketplace-home.module.css";

export const MarketplaceHome = memo(function MarketplaceHome() {
  const vendorAuth = useContext(VendorAuthContext);
  const navigate = useNavigate();

  const [currentView, setCurrentView] = useState<"products" | "confirmation">(
    "products",
  );
  const [completedOrder, setCompletedOrder] = useState<OrderDetails | null>(
    null,
  );

  const handleStartNewOrder = useHandler(() => {
    setCompletedOrder(null);
    setCurrentView("products");
  });

  const handleSignIn = useHandler(() => {
    navigate("/login");
  });

  return (
    <main className="flex-1 w-full">
      {vendorAuth && currentView === "products" && (
        <div className={marketplaceHomeCss.bannerContainer}>
          <div className={marketplaceHomeCss.bannerWrapper}>
            <img
              alt="Redo Marketplace Banner"
              className={marketplaceHomeCss.bannerImage}
              loading="eager"
              src={marketplaceBannerUrl}
            />
          </div>
        </div>
      )}
      <div className="px-4 xl:px-8">
        {currentView === "confirmation" ? (
          <Flex align="center" dir="column" gap="lg" p="xl">
            <Text color="green" fontWeight="bold">
              🎉 Order Confirmed!
            </Text>
            <Text fontSize="lg" textAlign="center">
              Thank you for your order! We'll send you a confirmation email
              shortly.
            </Text>
            {completedOrder && (
              <Flex align="center" dir="column" gap="sm">
                <Text fontSize="md" fontWeight="semibold">
                  Order Total: ${completedOrder.totalPrice.toFixed(2)}
                </Text>
                <Text color="gray" fontSize="sm">
                  {completedOrder.items.length} item(s) ordered
                </Text>
              </Flex>
            )}
            <RedoButton
              hierarchy="primary"
              onClick={handleStartNewOrder}
              size="lg"
              text="Continue Shopping"
            />
          </Flex>
        ) : !vendorAuth ? (
          <Flex
            align="center"
            dir="column"
            gap="lg"
            justify="center"
            p="xl"
            style={{ minHeight: "400px" }}
          >
            <Text color="gray" fontSize="xl" fontWeight="semibold">
              🛍️ Welcome to Redo Marketplace
            </Text>
            <Text color="gray" fontSize="md" textAlign="center">
              Sign in to browse and purchase products from our marketplace
            </Text>
            <RedoButton
              hierarchy="primary"
              onClick={handleSignIn}
              size="lg"
              text="Sign In to Browse Marketplace"
            />
          </Flex>
        ) : (
          <div className="w-full">
            <MarketplaceProductsGrid />
          </div>
        )}
      </div>
    </main>
  );
});
